// 首页展示配置模块自定义hooks

import { useMemo, useCallback, useRef, useState, useEffect } from "react";
import { useHomeDisplayStore } from "../stores";
import { homeDisplayService } from "../services";
import {
  DisplayPosition,
  TimingType,
  DisplayItem,
  PopupTiming,
  PopupQueueItem,
  GroupedDisplayItems,
  ActivityType,
  ActivitiesConfig,
  FooterConfig,
  LoginRegisterConfig,
} from "../types";
import { ROUTES } from "../../../utils/navigation";
import { openGlobalRulesDialog } from "../../../components/RulesDialog/rulesDialogTrigger";
import { POPUP_IDS } from "../../../config/popupConfig";

// ===========================
// 过滤工具函数
// ===========================

// 需要验证 activityId 的活动类型
const ACTIVITY_TYPES_NEED_VALIDATION = [
  ActivityType.DAILY_RECHARGE,
  ActivityType.RECHARGE_ACTIVITY,
  ActivityType.BET_TASK,
  ActivityType.NINE_GRID_ACTIVITY,
  ActivityType.RESCUE_FUND,
];

/** 救援金 banner 点击时派发此事件，payload 为 { taskId }，由 useRescueFundPopup 监听并打开弹窗 */
export const OPEN_RESCUE_FUND_EVENT = "open-rescue-fund";

// 需要过滤 activityItems 的弹窗时机类型
const TIMING_TYPES_NEED_FILTER = [
  TimingType.BALANCE_INSUFFICIENT,
  TimingType.ACTIVITY_QUALIFICATION,
];

/**
 * 检查 activityId 是否存在于对应的 activities 列表中
 * @public 导出供弹窗hooks使用
 */
export const isActivityIdValid = (
  activityId: string | undefined,
  activityType: ActivityType | undefined,
  activities: ActivitiesConfig | undefined
): boolean => {
  if (!activityId || !activityType || !activities) return true;

  if (!ACTIVITY_TYPES_NEED_VALIDATION.includes(activityType)) {
    return true;
  }

  let activityList: any[] = [];
  switch (activityType) {
    case ActivityType.RECHARGE_ACTIVITY:
      activityList =
        activities.RECHARGE_ACTIVITY?.list?.filter(
          item => item.activityGroupId === activityId
        ) || [];

      break;
    case ActivityType.DAILY_RECHARGE:
      activityList =
        activities.DAILY_RECHARGE?.list?.filter(item => {
          // 检查活动ID是否匹配
          if (item.id !== activityId) return false;
          // 检查活动是否还在有效期内（countdownSeconds > 0）
          const countdownSeconds =
            item.dailyRechargeData?.countdownSeconds ?? 0;
          return countdownSeconds > 0;
        }) || [];
      break;
    case ActivityType.BET_TASK:
      activityList =
        activities.BET_TASK?.list?.filter(
          item => item?.taskGroupId === activityId
        ) || [];
      break;
    case ActivityType.RESCUE_FUND:
      activityList =
        activities.RESCUE_FUND?.list?.filter(
          item => item?.taskGroupId === activityId || item?.id === activityId
        ) || [];
      break;
    case ActivityType.NINE_GRID_ACTIVITY:
      activityList =
        activities.NINE_GRID_ACTIVITY?.list?.filter(
          item => (item?.activityGroupId || item?.id) === activityId // ✅ 兼容两种字段名
        ) || [];
      break;
    default:
      return true;
  }

  return activityList.length > 0;
};

/**
 * 过滤 displayItems，移除在 activities 中不存在的活动
 */
const filterDisplayItems = (
  items: DisplayItem[],
  activities: ActivitiesConfig | undefined
): DisplayItem[] => {
  if (!activities) return items;
  return items.filter(item => {
    const activityType = (item as any)?.activityType as
      | ActivityType
      | undefined;
    const activityId = (item as any)?.activityId as string | undefined;

    return isActivityIdValid(activityId, activityType, activities);
  });
};

/**
 * 过滤 popupTimings，移除在 activities 中不存在的活动项
 */
const filterPopupTimings = (
  timings: PopupTiming[],
  activities: ActivitiesConfig | undefined
): PopupTiming[] => {
  if (!activities) return timings;

  return timings.map(timing => {
    if (!TIMING_TYPES_NEED_FILTER.includes(timing.timingType)) {
      return timing;
    }

    const filteredActivityItems = timing.activityItems.filter(item => {
      return isActivityIdValid(item.activityId, item.activityType, activities);
    });

    return {
      ...timing,
      activityItems: filteredActivityItems,
    };
  });
};

// ===========================
// 基础选择器 Hooks
// ===========================

/**
 * 获取首页展示配置是否已加载完成
 */
export const useIsHomeDisplayReady = () =>
  useHomeDisplayStore(state => state.isConfigLoaded);

/**
 * 获取 displayItems（过滤掉在 activities 中不存在的活动）
 */
export const useDisplayItems = () =>
  useHomeDisplayStore(state => {
    return filterDisplayItems(
      state.data?.displayItems || [],
      state.data?.activities
    );
  });

/**
 * 获取 popupTimings（过滤掉在 activities 中不存在的活动项）
 */
export const usePopupTimings = () =>
  useHomeDisplayStore(state => {
    return filterPopupTimings(
      state.data?.popupTimings || [],
      state.data?.activities
    );
  });

/**
 * 获取 popupPriorities
 */
export const usePopupPriorities = () =>
  useHomeDisplayStore(state => state.data?.popupPriorities || []);

/**
 * 获取 activities
 */
export const useActivities = () =>
  useHomeDisplayStore(state => state.data?.activities);

/**
 * 获取充值活动列表 (RECHARGE_ACTIVITY)
 */
export const useRechargeActivityList = () =>
  useHomeDisplayStore(
    state => state.data?.activities?.RECHARGE_ACTIVITY?.list || []
  );

/**
 * 获取7日充值活动 (DAILY_RECHARGE)
 */
export const useDailyRechargeActivity = () =>
  useHomeDisplayStore(
    state => state.data?.activities?.DAILY_RECHARGE?.list || []
  );

/**
 * 获取九宫格活动 (NINE_GRID_ACTIVITY)
 */
export const useNineGridActivity = () =>
  useHomeDisplayStore(
    state => state.data?.activities?.NINE_GRID_ACTIVITY?.list || []
  );

/**
 * 智能选择当前应该展示的九宫格活动
 * 优先返回用户正在参与的活动（periodEndTime 未过期），避免新配置的高优先级活动
 * 在用户当前活动结束前抢占显示。
 */
export const useActiveNineGridActivity = () => {
  const list = useNineGridActivity();
  return useMemo(() => {
    if (list.length <= 1) return list[0] ?? undefined;
    const now = Date.now();
    const active = list.find(
      item =>
        item.userProgress &&
        item.userProgress.periodEndTime > now &&
        item.userProgress.participationStatus === "ACTIVE"
    );
    return active ?? list[0];
  }, [list]);
};

/**
 * 获取投注任务 (BET_TASK)
 * 返回 list、isNewDailyBetTask、resetNewTaskFlag
 */
export const useBetTaskActivity = () => {
  const list = useHomeDisplayStore(
    state => state.data?.activities?.BET_TASK?.list || []
  );
  const prevTaskIdRef = useRef<string | undefined>(undefined);
  const [isNewDailyBetTask, setIsNewDailyBetTask] = useState(false);

  // 获取当前任务ID
  const currentTaskId = list[0]?.id;

  // 检测任务ID变化
  useEffect(() => {
    if (prevTaskIdRef.current !== undefined && currentTaskId !== undefined) {
      // 只有当之前有任务ID且新任务ID不同时，才标记为新任务
      if (prevTaskIdRef.current !== currentTaskId) {
        setIsNewDailyBetTask(true);
      }
    }
    prevTaskIdRef.current = currentTaskId;
  }, [currentTaskId]);
  const taskFinished =
    !!list[0]?.betData?.currentBetAmount &&
    list[0]?.betData?.taskList?.every(
      task =>
        list[0]?.betData?.currentBetAmount &&
        list[0]?.betData?.currentBetAmount >= task.betAmount
    );

  // 重置新任务标记
  const resetNewTaskFlag = useCallback(() => {
    setIsNewDailyBetTask(false);
  }, []);
  return {
    list,
    isNewDailyBetTask,
    resetNewTaskFlag,
    taskFinished,
  };
};

/**
 * 根据 activityType 获取对应的 popupPriority（sort值）
 */
export const useActivityPopupPriority = (
  activityType: ActivityType | undefined,
  defaultPriority: number,
  timingType: TimingType,
  activityId?: string | undefined
): number => {
  return useHomeDisplayStore(state => {
    if (!activityType) return defaultPriority;
    const timings = (state.data?.popupTimings || []).filter(
      timing => timing.timingType === timingType
    );
    for (const timing of timings) {
      const matchedItem = timing.activityItems.find(
        item =>
          item.activityType === activityType &&
          (!activityId || item.activityId === activityId)
      );
      if (matchedItem) {
        const sort = matchedItem.sort ?? defaultPriority;

        if (timingType === TimingType.ACTIVITY_QUALIFICATION) {
          return sort * 100;
        }
        return sort;
      }
    }

    return defaultPriority;
  });
};

/**
 * 根据 activityId 获取对应的 popupPriority（sort值）
 */
export const useActivityPopupPriorityById = (
  activityId: string | undefined,
  defaultPriority: number
): number => {
  return useHomeDisplayStore(state => {
    if (!activityId) return defaultPriority;

    const timings = state.data?.popupTimings || [];

    for (const timing of timings) {
      const matchedItem = timing.activityItems.find(
        item => item.activityId === activityId
      );
      if (matchedItem) {
        return matchedItem.popupPriority ?? matchedItem.sort ?? defaultPriority;
      }
    }

    return defaultPriority;
  });
};

/**
 * 获取下载弹窗说明文案（来自 home_display/config 的 downloadPagePopupConfig）
 * 支持顶层或 platformLogo 内，有配置则返回文案，否则返回 null
 */
export const useDownloadPopupNotice = (): string | null => {
  const raw = useHomeDisplayStore(state => {
    const d = state.data;
    return (
      d?.downloadPagePopupConfig ?? d?.platformLogo?.downloadPagePopupConfig
    );
  });
  const notice = typeof raw === "string" && raw.trim() ? raw.trim() : null;
  return notice;
};

/**
 * 获取平台Logo配置
 */
export const usePlatformLogo = () =>
  useHomeDisplayStore(state => state.data?.platformLogo || null);

/**
 * 是否启用下载 App 弹窗（来自 home_display/config 的 platformLogo.enableDownloadAppPopup）
 * 为 true 时才会显示下载 App 弹窗
 */
export const useEnableDownloadAppPopup = (): boolean => {
  const v = useHomeDisplayStore(
    state => state.data?.platformLogo?.enableDownloadAppPopup
  );
  return v === true;
};

/**
 * 是否在首页顶部显示下载 App 浮动条（来自 platformLogo.enableDownloadAppFloatingOnHomeTop）
 * 为 true 时才会显示顶部下载条
 */
export const useEnableDownloadAppFloatingOnHomeTop = (): boolean | null => {
  const isConfigLoaded = useHomeDisplayStore(state => state.isConfigLoaded);
  const v = useHomeDisplayStore(
    state => state.data?.platformLogo?.enableDownloadAppFloatingOnHomeTop
  );
  if (!isConfigLoaded) return null; // null = 配置还在加载中
  return v === true;
};

/**
 * 获取客服配置列表（按 sort 排序）
 */
export const useCustomerServices = () =>
  useHomeDisplayStore(state => {
    const services = state.data?.customerServices || [];
    return [...services].sort((a, b) => a.sort - b.sort);
  });

/**
 * 获取代理邀请配置
 */
export const useAgentInvitation = () =>
  useHomeDisplayStore(state => state.data?.agentInvitation || null);

/**
 * 获取 Footer 配置
 */
export const useFooterConfig = (): FooterConfig | null =>
  useHomeDisplayStore(state => state.data?.footer || null);

/**
 * 获取注册配置（旧版）
 */
export const useRegisterDisplayConfig = () =>
  useHomeDisplayStore(state => state.data?.displayConfig || null);

/**
 * 获取当前域名匹配的登录注册配置
 */
export const useLoginRegisterConfig = () => {
  return useHomeDisplayStore(state => {
    const data = state.data as unknown as Record<string, unknown> | undefined;
    const configs = (state.data?.loginRegisterConfigs ??
      data?.login_register_configs) as LoginRegisterConfig[] | undefined;

    if (!configs || configs.length === 0) {
      return null;
    }

    // 获取当前域名（本地环境固定为 zagotest.com）
    const hostname = window.location.hostname;
    const currentDomain =
      hostname === "localhost" || hostname === "127.0.0.1"
        ? "zagotest.com"
        : hostname;

    // 1. 先找 MANUAL 类型且域名匹配的配置（域名做字符串比较，兼容后端返回类型）
    const manualConfig = configs.find(
      config =>
        config.configType === "MANUAL" &&
        config.domainId?.some((domain: unknown) => {
          const d = String(domain ?? "").trim();
          return (
            d === currentDomain ||
            d === `www.${currentDomain}` ||
            currentDomain === `www.${d}`
          );
        })
    );

    if (manualConfig) {
      return manualConfig;
    }

    // 2. 如果没有匹配的 MANUAL，找 SYSTEM_DEFAULT
    const defaultConfig = configs.find(
      config => config.configType === "SYSTEM_DEFAULT"
    );

    return defaultConfig;
  });
};

/**
 * 手机号重置密码 IP 是否处于冷却中（尝试次数达上限）
 * 来自 home_display/config 的 resetPasswordIpInCooldown
 */
export const useResetPasswordIpInCooldown = () =>
  useHomeDisplayStore(state => state.data?.resetPasswordIpInCooldown === true);

/**
 * 邮箱重置密码 IP 是否处于冷却中（尝试次数达上限）
 * 来自 home_display/config 的 resetPasswordEmailIpInCooldown
 */
export const useResetPasswordEmailIpInCooldown = () =>
  useHomeDisplayStore(
    state => state.data?.resetPasswordEmailIpInCooldown === true
  );

/**
 * 在线客服链接（customerCategory 为 ONLINE_SERVICE 的 licenseId）
 * 用于「次数过多」等弹窗中的「联系客服」链接
 */
export const useOnlineServiceSupportUrl = (): string | null =>
  useHomeDisplayStore(state => {
    const service = state.data?.customerServices?.find(
      s => s.customerCategory === "ONLINE_SERVICE"
    );
    return service?.licenseId ?? null;
  });

/**
 * 获取KYC配置
 */
export const useKycConfig = () =>
  useHomeDisplayStore(state => state.data?.kycConfig || null);

/**
 * 获取当前域名匹配到的 KycDomainConfig
 * - domainRestricted 为 false 或未设置时，返回一个 domain 为空、withdrawGuideCount 为 0 的默认对象（表示不限制）
 * - domainRestricted 为 true 时，返回 domainConfigs 中匹配当前域名的项；无匹配返回 null
 */
export const useMatchedKycDomainConfig = () => {
  const kycConfig = useKycConfig();
  return useMemo(() => {
    if (!kycConfig?.domainRestricted)
      return { domain: "", withdrawGuideCount: 0 };
    const configs = kycConfig.domainConfigs;
    if (!configs || configs.length === 0) return null;
    const hostname = window.location.hostname;
    const currentDomain =
      hostname === "localhost" || hostname === "127.0.0.1"
        ? "zagotest.com"
        : hostname;
    return (
      configs.find(c => {
        const domain = String(c.domain ?? "").trim();
        return (
          domain === currentDomain ||
          domain === `www.${currentDomain}` ||
          currentDomain === `www.${domain}`
        );
      }) ?? null
    );
  }, [kycConfig]);
};

/**
 * 判断当前域名是否匹配KYC域名限制（布尔值快捷 hook）
 */
export const useKycDomainMatch = () => {
  return useMatchedKycDomainConfig() !== null;
};

// ===========================
// 业务逻辑 Hooks
// ===========================

/**
 * 获取指定位置的Banner列表
 * @param position 展示位置
 * @returns DisplayItem数组（已排序）
 */
export function useDisplayItemsByPosition(position: DisplayPosition) {
  const items = useDisplayItems();
  return useMemo(
    () => homeDisplayService.getDisplayItemsByPosition(items, position),
    [items, position]
  );
}

/**
 * 获取首页主Banner列表
 */
export function useHomeMainBanners() {
  return useDisplayItemsByPosition(DisplayPosition.HOME_MAIN_BANNER);
}

/**
 * 获取首页次级Banner（左）
 */
export function useHomeSubBannerLeft() {
  const items = useDisplayItemsByPosition(DisplayPosition.HOME_SUB_BANNER_LEFT);
  return items;
}

/**
 * 获取首页次级Banner（右）
 */
export function useHomeSubBannerRight() {
  const items = useDisplayItemsByPosition(
    DisplayPosition.HOME_SUB_BANNER_RIGHT
  );
  return items;
}

/**
 * 获取Events列表Banner
 */
export function useEventsListBanners() {
  return useDisplayItemsByPosition(DisplayPosition.EVENTS_LIST);
}

/**
 * 获取右下角浮动图标
 */
export function useFloatingIcon() {
  const items = useDisplayItemsByPosition(DisplayPosition.FLOATING_ICON);
  return items;
}

/**
 * 获取左下角浮动图标
 */
export function useLeftBottomFloatingIcon() {
  const items = useDisplayItemsByPosition(
    DisplayPosition.LEFT_BOTTOM_FLOATING_ICON
  );
  return items;
}

/**
 * 获取所有Display Items按位置分组
 */
export function useGroupedDisplayItems(): GroupedDisplayItems {
  const items = useDisplayItems();

  return useMemo(
    () => homeDisplayService.groupDisplayItemsByPosition(items),
    [items]
  );
}

/**
 * 获取指定时机的弹窗配置
 * @param timingType 弹窗时机类型
 */
export function usePopupTimingByType(timingType: TimingType) {
  const timings = usePopupTimings();

  return useMemo(
    () => homeDisplayService.getPopupTimingByType(timings, timingType),
    [timings, timingType]
  );
}

/**
 * 获取进入首页时的弹窗队列
 * @returns 排序后的弹窗队列
 */
export function useEnterHomePagePopups(): PopupQueueItem[] {
  const timing = usePopupTimingByType(TimingType.ENTER_HOME_PAGE);
  const priorities = usePopupPriorities();

  return useMemo(() => {
    if (!timing) return [];

    const shownRecords = homeDisplayService.getTodayShownPopups();
    return homeDisplayService.getPopupQueue(timing, priorities, shownRecords);
  }, [timing, priorities]);
}

/**
 * DisplayItem点击处理hook - 带默认行为
 * @returns 点击处理函数
 */
export function useDisplayItemClick() {
  const dailyRechargeTasks = useDailyRechargeActivity();
  const dailyRecharge = dailyRechargeTasks[0];
  const handleShowRules = useCallback(() => {
    const title = dailyRecharge.displayConfig?.landingPageTitle;
    const content = dailyRecharge.displayConfig?.landingPageContent || "";
    openGlobalRulesDialog(title, content);
  }, [dailyRecharge]);
  const gridLotteryActivity = useActiveNineGridActivity();
  const handleShowGridLottery = useCallback(() => {
    const rawTitle = gridLotteryActivity?.entryPoint?.landingPageTitle || "";
    // ✅ 移除分隔符（|、｜、换行符），用空格连接
    const title = rawTitle
      .replace(/[|｜\n]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    const content = gridLotteryActivity?.entryPoint?.landingPageContent || "";
    openGlobalRulesDialog(title, content);
  }, [gridLotteryActivity]);
  return useCallback(
    (
      item: DisplayItem,
      navigate: (path: string) => void,
      triggerPopup: (id: string) => void,
      options?: {
        onActivity?: (activityId: string, item: DisplayItem) => void;
        onRedirect?: (url: string, item: DisplayItem) => void;
        onContent?: (title: string, content: string, item: DisplayItem) => void;
        usePopupMode?: boolean;
      }
    ) => {
      const action = homeDisplayService.getDisplayItemClickAction(item);
      switch (action.type) {
        case "activity": {
          const activityId = action.value;
          const groupId = action.groupId;
          const activityType = action.activityType;
          // 如果提供了自定义处理，优先使用
          if (options?.onActivity) {
            options.onActivity(activityId, item);
            return;
          }

          // 默认活动处理逻辑
          switch (activityType) {
            case ActivityType.RECHARGE_ACTIVITY:
              if (options?.usePopupMode) {
                // 从 item 中获取 banner.id 来构造弹窗ID
                if (groupId) {
                  const popupId = `recharge-promotion-auto-${groupId}`;
                  triggerPopup(popupId);
                }
              } else {
                navigate(`${ROUTES.RECHARGE_PROMO}?activityId=${groupId}`);
              }
              break;
            case ActivityType.BET_TASK:
              if (options?.usePopupMode) {
                triggerPopup(POPUP_IDS.BET_ACTIVITY);
              } else {
                navigate(`${ROUTES.EVENT_DETAILS}?type=bet`);
              }
              break;
            case ActivityType.DAILY_RECHARGE:
              if (options?.usePopupMode) {
                triggerPopup(POPUP_IDS.SEVEN_DAY_RECHARGE);
              } else {
                handleShowRules();
              }
              break;
            case ActivityType.RESCUE_FUND:
              // 派发事件，由 useRescueFundPopup 监听并设置 taskId 后打开弹窗
              window.dispatchEvent(
                new CustomEvent(OPEN_RESCUE_FUND_EVENT, {
                  detail: { taskId: activityId },
                })
              );
              break;
            case ActivityType.NINE_GRID_ACTIVITY:
              if (options?.usePopupMode) {
                triggerPopup(POPUP_IDS.GRID_LOTTERY);
              } else {
                handleShowGridLottery();
              }
              // 九宫格活动 - 总是触发弹窗
              // triggerPopup(POPUP_IDS.GRID_LOTTERY);
              break;
            default:
              console.log("activityType", activityType);
              console.log("未处理的活动ID:", activityId);
          }
          break;
        }

        case "redirect": {
          const url = action.value;

          // 如果提供了自定义处理，优先使用
          if (options?.onRedirect) {
            options.onRedirect(url, item);
            return;
          }

          // 默认跳转处理逻辑
          if (url.startsWith("http://") || url.startsWith("https://")) {
            window.open(url, "_blank");
          } else if (url.startsWith("/")) {
            navigate(url);
          } else if (url.startsWith("#")) {
            triggerPopup(url.slice(1));
          } else {
            window.location.href = url;
          }
          break;
        }

        case "content": {
          // 使用全局 RulesDialog
          openGlobalRulesDialog(action.title || "", action.content || "");
          // 如果提供了自定义处理，也调用它
          if (options?.onContent) {
            options.onContent(action.title || "", action.content || "", item);
          }
          break;
        }
        default:
          console.log("action", action);
          console.log("未处理的动作:", action.type);
          break;
      }
    },
    []
  );
}

/**
 * 弹窗记录管理hook
 */
export function usePopupRecords() {
  const getTodayShown = useCallback(() => {
    return homeDisplayService.getTodayShownPopups();
  }, []);

  const markAsShown = useCallback((id: string, type: string) => {
    homeDisplayService.markPopupAsShown(id, type);
  }, []);

  const cleanup = useCallback(() => {
    homeDisplayService.cleanupOldPopupRecords();
  }, []);

  return {
    getTodayShown,
    markAsShown,
    cleanup,
  };
}
